import React, { useState } from 'react';
import { Phone, User, MessageSquare, Briefcase, X, Loader2, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { useLang } from '../context/LanguageContext';
import { QRCodeSVG } from 'qrcode.react';
import { trackGenerateLead, trackWhatsAppClick, trackConsultationBooked } from '../utils/analytics';

const API = process.env.REACT_APP_BACKEND_URL;

const services = [
  'Kundli Analysis / कुंडली विश्लेषण',
  'Horoscope / राशिफल',
  'Kundli Matching / कुंडली मिलान',
  'Vastu Consultation / वास्तु परामर्श',
  'Career Guidance / करियर मार्गदर्शन',
  'Gemstone Advice / रत्न परामर्श',
  'Grah Shanti Puja / ग्रह शांति पूजा',
  'Family Problems / पारिवारिक समस्या',
  'Other / अन्य',
];

export default function BookConsultation() {
  const [form, setForm] = useState({ name: '', phone: '', email: '', service: services[0], message: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const { lang, t } = useLang();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) return;
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/api/consultations`, form);
      // GA4: consultation_booked (Acharya Guruji funnel) + legacy generate_lead for Ads import
      trackConsultationBooked({
        form_name: 'book_consultation',
        service: form.service,
        value: 5100,
      });
      trackGenerateLead('book_consultation', {
        service: form.service,
        value: 5100,
      });
      setSuccess(true);
      setForm({ name: '', phone: '', email: '', service: services[0], message: '' });
    } catch (err) {
      setError('Booking failed. Please try again or contact via WhatsApp.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="book-consultation" className="section-padding relative overflow-hidden" data-testid="book-consultation-section">
      <div className="absolute inset-0 cosmic-bg-alt" />
      <div className="absolute top-1/4 right-0 w-80 h-80 bg-gold/8 rounded-full blur-[120px]" />
      <div className="absolute bottom-1/4 left-0 w-80 h-80 bg-purple/10 rounded-full blur-[100px]" />

      <div className="container-custom relative z-10">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-xs tracking-[0.3em] uppercase text-gold font-semibold font-body">
            {t('book.title')}
          </span>
          <h2 className="font-heading text-4xl md:text-5xl font-medium text-text-main mt-4 mb-4 tracking-tight">
            {lang === 'hi' ? 'परामर्श' : 'Book a'} <span className="text-gold-gradient">{lang === 'hi' ? 'बुक करें' : 'Consultation'}</span>
          </h2>
          <p className="text-text-muted">
            {lang === 'hi' ? 'नीचे फॉर्म भरें, हम जल्द ही आपसे संपर्क करेंगे।' : 'Fill the form below and we will get back to you shortly.'}
          </p>
        </div>

        <div className="max-w-2xl mx-auto">
          {success ? (
            <div className="glass-card p-10 text-center animate-fade-in-up" data-testid="booking-success">
              <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
              <h3 className="font-heading text-2xl text-text-main mb-2">
                {lang === 'hi' ? 'बुकिंग सफल!' : 'Booking Confirmed!'}
              </h3>
              <p className="text-text-muted mb-2">
                {lang === 'hi' ? 'धन्यवाद! हम जल्द ही आपसे संपर्क करेंगे।' : 'Thank you! We will contact you soon.'}
              </p>
              <p className="text-text-muted/70 text-sm mb-6">
                {lang === 'hi' ? 'अभी payment करने के लिए नीचे Pay Now बटन दबाएं' : 'Click Pay Now below to make the payment'}
              </p>

              {/* UPI Payment Section */}
              <div className="bg-[#3d101e] border border-yellow-800/30 rounded-xl p-5 mb-6" data-testid="upi-payment-section">
                <p className="text-yellow-300 font-semibold text-lg mb-4">
                  {lang === 'hi' ? 'भुगतान करें' : 'Make Payment'}
                </p>

                {/* QR Code */}
                <div className="flex justify-center mb-4">
                  <div className="bg-white p-3 rounded-xl" data-testid="upi-qr-code">
                    <QRCodeSVG
                      value="upi://pay?pa=9911194198@ptyes&pn=Vivek%20Mudgal%20Astrologer&cu=INR"
                      size={160}
                      level="H"
                      bgColor="#ffffff"
                      fgColor="#3d101e"
                    />
                  </div>
                </div>
                <p className="text-gray-400 text-xs mb-4">
                  {lang === 'hi' ? 'किसी भी UPI app से QR Code scan करें' : 'Scan QR Code with any UPI app'}
                </p>

                {/* Payment App Buttons */}
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <a
                    href="tez://upi/pay?pa=9911194198@ptyes&pn=Vivek%20Mudgal%20Astrologer&cu=INR"
                    className="flex items-center justify-center gap-2 px-4 py-2.5 bg-[#0f0508] border border-yellow-900/30 text-white rounded-lg text-sm font-medium hover:border-yellow-600 transition-all"
                    data-testid="gpay-btn"
                  >
                    <svg viewBox="0 0 24 24" className="w-5 h-5"><circle cx="12" cy="12" r="11" fill="#4285F4"/><text x="12" y="16" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">G</text></svg>
                    Google Pay
                  </a>
                  <a
                    href="phonepe://pay?pa=9911194198@ptyes&pn=Vivek%20Mudgal%20Astrologer&cu=INR"
                    className="flex items-center justify-center gap-2 px-4 py-2.5 bg-[#0f0508] border border-yellow-900/30 text-white rounded-lg text-sm font-medium hover:border-purple-500 transition-all"
                    data-testid="phonepe-btn"
                  >
                    <svg viewBox="0 0 24 24" className="w-5 h-5"><circle cx="12" cy="12" r="11" fill="#5F259F"/><text x="12" y="16" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">P</text></svg>
                    PhonePe
                  </a>
                  <a
                    href="paytmmp://pay?pa=9911194198@ptyes&pn=Vivek%20Mudgal%20Astrologer&cu=INR"
                    className="flex items-center justify-center gap-2 px-4 py-2.5 bg-[#0f0508] border border-yellow-900/30 text-white rounded-lg text-sm font-medium hover:border-blue-500 transition-all"
                    data-testid="paytm-btn"
                  >
                    <svg viewBox="0 0 24 24" className="w-5 h-5"><circle cx="12" cy="12" r="11" fill="#00BAF2"/><text x="12" y="16" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">P</text></svg>
                    Paytm
                  </a>
                  <a
                    href="https://wa.me/919311369729?text=Namaste!%20I%20booked%20a%20consultation%20and%20want%20to%20make%20payment."
                    target="_blank" rel="noopener noreferrer"
                    onClick={() => trackWhatsAppClick('book_consult_pay_whatsapp')}
                    className="flex items-center justify-center gap-2 px-4 py-2.5 bg-[#0f0508] border border-yellow-900/30 text-white rounded-lg text-sm font-medium hover:border-green-500 transition-all"
                    data-testid="pay-whatsapp-btn"
                  >
                    <svg viewBox="0 0 24 24" className="w-5 h-5"><circle cx="12" cy="12" r="11" fill="#25D366"/><text x="12" y="16" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">W</text></svg>
                    WhatsApp
                  </a>
                </div>
                <p className="text-gray-500 text-xs">UPI ID: 9911194198@ptyes</p>
              </div>

              <button
                onClick={() => setSuccess(false)}
                className="btn-gold-outline"
                data-testid="book-again-btn"
              >
                {lang === 'hi' ? 'और बुकिंग करें' : 'Book Another'}
              </button>
            </div>
          ) : (
            <div className="glass-card p-8 md:p-10">
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="consultation-form">
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm text-text-muted mb-2 font-medium">Name / नाम *</label>
                    <div className="relative">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
                      <input
                        type="text" value={form.name} required
                        onChange={(e) => setForm({...form, name: e.target.value})}
                        placeholder="Your Name" className="pl-12"
                        data-testid="consult-name-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-text-muted mb-2 font-medium">Phone / फोन *</label>
                    <div className="relative">
                      <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
                      <input
                        type="tel" value={form.phone} required
                        onChange={(e) => setForm({...form, phone: e.target.value})}
                        placeholder="+91 XXXXX XXXXX" className="pl-12"
                        data-testid="consult-phone-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm text-text-muted mb-2 font-medium">Email (Optional)</label>
                    <input
                      type="email" value={form.email}
                      onChange={(e) => setForm({...form, email: e.target.value})}
                      placeholder="your@email.com"
                      data-testid="consult-email-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-text-muted mb-2 font-medium">Service / सेवा *</label>
                    <div className="relative">
                      <Briefcase className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
                      <select
                        value={form.service}
                        onChange={(e) => setForm({...form, service: e.target.value})}
                        className="pl-12"
                        data-testid="consult-service-select"
                      >
                        {services.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-text-muted mb-2 font-medium">Message / संदेश</label>
                  <div className="relative">
                    <MessageSquare className="absolute left-4 top-4 w-5 h-5 text-text-muted" />
                    <textarea
                      value={form.message} rows={3}
                      onChange={(e) => setForm({...form, message: e.target.value})}
                      placeholder="Describe your concern..." className="pl-12"
                      data-testid="consult-message-input"
                    />
                  </div>
                </div>

                {error && <p className="text-red-400 text-sm text-center">{error}</p>}

                <button
                  type="submit" disabled={loading || !form.name || !form.phone}
                  className="btn-gold w-full flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="consult-submit-btn"
                >
                  {loading ? (
                    <><Loader2 className="w-5 h-5 animate-spin" /> Booking...</>
                  ) : (
                    'Book Consultation / परामर्श बुक करें'
                  )}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
